import validator from 'validator';
import BotResponseTreeNode from '../../../types/BotBuilder/BotResponseTreeNode';
import IntentTreeNode from '../../../types/BotBuilder/IntentTreeNode';
import MessageBlockTreeNode from '../../../types/BotBuilder/MessageBlockTreeNode';
import ResponseCardsTreeNode from '../../../types/BotBuilder/ResponseCardsTreeNode';
import GoToIntentTreeNode from '../../../types/BotBuilder/GoToIntentTreeNode';
import CollectVariableTreeNode from '../../../types/BotBuilder/CollectVariableTreeNode';
import ConditionalTreeNode from '../../../types/BotBuilder/ConditionalTreeNode';
import ComparisonOperator from '../../../types/BotBuilder/ComparisonOperator';
import GenerativeAiTreeNode from '../../../types/BotBuilder/GenerativeAiTreeNode';
import CalculationTreeNode from '../../../types/BotBuilder/CalculationTreeNode';
import SendEmailTreeNode from '../../../types/BotBuilder/SendEmailTreeNode';
import SendSmsTreeNode from '../../../types/BotBuilder/SendSmsTreeNode';
import DeleteVariableTreeNode from '../../../types/BotBuilder/DeleteVariableTreeNode';
import CodeBlockTreeNode from '../../../types/BotBuilder/CodeBlockTreeNode';
import LlmTreeNode from '../../../types/BotBuilder/LlmTreeNode';
import DoNothingTreeNode from '../../../types/BotBuilder/DoNothingTreeNode';
import SlotValueType from '../../../types/BotBuilder/SlotValueType';
import {
  extractUrlsFromMarkdown,
  MAX_URL_LENGTH
} from '../../../utils/markdownUtils';
import { validateEnvironmentVariables } from './environmentVariableValidation';

class NodeValidator {
  private errors: string[] = [];

  private readonly visitedNodes: Set<string> = new Set();

  private readonly currentIntentId: number | null;

  constructor(currentIntentId: number | null = null) {
    this.currentIntentId = currentIntentId;
  }

  private addError(error: string): void {
    this.errors.push(error);
  }

  private validateNode(node: BotResponseTreeNode): void {
    if (this.visitedNodes.has(node.id)) {
      return;
    }

    this.visitedNodes.add(node.id);

    if (node.content instanceof IntentTreeNode) {
      this.validateRootNode(node);
    } else if (node.content instanceof MessageBlockTreeNode) {
      this.validateResponseBlock(node);
    } else if (node.content instanceof ResponseCardsTreeNode) {
      this.validateResponseCards(node);
    } else if (node.content instanceof GoToIntentTreeNode) {
      this.validateGoToIntent(node);
    } else if (node.content instanceof ConditionalTreeNode) {
      this.validateConditionalNode(node);
    } else if (node.content instanceof CollectVariableTreeNode) {
      this.validateCollectVariable(node);
    } else if (node.content instanceof CalculationTreeNode) {
      this.validateCalculationNode(node);
    } else if (node.content instanceof SendEmailTreeNode) {
      this.validateSendEmail(node);
    } else if (node.content instanceof SendSmsTreeNode) {
      this.validateSendSms(node);
    } else if (node.content instanceof DeleteVariableTreeNode) {
      this.validateDeleteVariable(node);
    } else if (node.content instanceof CodeBlockTreeNode) {
      this.validateCodeBlock(node);
    } else if (node.content instanceof LlmTreeNode) {
      this.validateLlmNode(node);
    }
  }

  private validateResponseCards(node: BotResponseTreeNode): void {
    const cardsNode = node.content as ResponseCardsTreeNode;
    const CONNECTION_ERROR_MESSAGE =
      'Follow up options must be connected to a chatbot response, collect variable, generative AI or redirect to a different question';

    let connectionErrorAdded = false;

    const nonEmptyItems = cardsNode.items.filter(
      (item) => item.text.trim() !== ''
    );
    const emptyItems = cardsNode.items.filter(
      (item) => item.text.trim() === ''
    );

    if (nonEmptyItems.length === 0) {
      this.addError(
        'Follow up options must contain at least one response option with text'
      );
      return;
    }

    if (emptyItems.length > 0) {
      this.addError(
        'Follow up options cannot contain empty response cards. Please remove empty cards or add text to them.'
      );
      return;
    }

    cardsNode.items.forEach((item) => {
      if (!item.nextStep) {
        if (!connectionErrorAdded) {
          this.addError(CONNECTION_ERROR_MESSAGE);
          connectionErrorAdded = true;
        }
        return;
      }

      const nextStepContent = item.nextStep.content;
      const isValidNextStep =
        nextStepContent instanceof ResponseCardsTreeNode ||
        nextStepContent instanceof MessageBlockTreeNode ||
        nextStepContent instanceof GoToIntentTreeNode ||
        nextStepContent instanceof ConditionalTreeNode ||
        nextStepContent instanceof CollectVariableTreeNode ||
        nextStepContent instanceof GenerativeAiTreeNode ||
        nextStepContent instanceof SendEmailTreeNode ||
        nextStepContent instanceof SendSmsTreeNode ||
        nextStepContent instanceof DeleteVariableTreeNode ||
        nextStepContent instanceof CodeBlockTreeNode ||
        nextStepContent instanceof LlmTreeNode ||
        nextStepContent instanceof DoNothingTreeNode;

      if (!isValidNextStep) {
        if (!connectionErrorAdded) {
          this.addError(CONNECTION_ERROR_MESSAGE);
          connectionErrorAdded = true;
        }
        return;
      }

      if (
        nextStepContent instanceof CollectVariableTreeNode &&
        NodeValidator.cardCannotTargetSlotType(nextStepContent.slotValueType)
      ) {
        this.addError(
          'Follow up options can only lead directly to an enumerated value variable. ' +
            'Other variable types need a chatbot response to prompt the visitor for input first.'
        );
        return;
      }

      this.validateNode(item.nextStep);
    });
  }

  private static cardCannotTargetSlotType(
    slotValueType: SlotValueType | null
  ): boolean {
    return (
      slotValueType !== null && slotValueType !== SlotValueType.ENUMERATED_VALUE
    );
  }

  private validateResponseBlock(node: BotResponseTreeNode): void {
    const messageNode = node.content as MessageBlockTreeNode;

    const hasEmptyContent = messageNode.messageBlocks.some(
      (block) => !block.content || block.content.trim() === ''
    );

    if (hasEmptyContent && !messageNode.nextStep) {
      this.addError(
        'Empty response block must be connected to another block or removed. Please add response text or connect this block to continue.'
      );
      return;
    }

    if (hasEmptyContent) {
      this.addError(
        'Chatbot response cannot be empty. Please add response text.'
      );
    }

    messageNode.messageBlocks.forEach((block) => {
      if (block.content) {
        const urls = extractUrlsFromMarkdown(block.content);
        const longUrls = urls.filter((url) => url.length > MAX_URL_LENGTH);
        if (longUrls.length > 0) {
          this.addError(
            `URL is too long (maximum ${MAX_URL_LENGTH} characters). Please use a shorter URL.`
          );
        }
      }
    });

    if (messageNode.nextStep) {
      const nextStepContent = messageNode.nextStep.content;

      if (
        !(nextStepContent instanceof MessageBlockTreeNode) &&
        !(nextStepContent instanceof ResponseCardsTreeNode) &&
        !(nextStepContent instanceof GoToIntentTreeNode) &&
        !(nextStepContent instanceof CollectVariableTreeNode) &&
        !(nextStepContent instanceof GenerativeAiTreeNode) &&
        !(nextStepContent instanceof SendEmailTreeNode) &&
        !(nextStepContent instanceof SendSmsTreeNode) &&
        !(nextStepContent instanceof DeleteVariableTreeNode) &&
        !(nextStepContent instanceof CodeBlockTreeNode) &&
        !(nextStepContent instanceof LlmTreeNode) &&
        !(nextStepContent instanceof DoNothingTreeNode)
      ) {
        this.addError(
          'A chatbot response can only be connected to another response block, a follow up options block, conditional, collect variable, generative AI or' +
            ' redirect to a different question'
        );
      }

      this.validateNode(messageNode.nextStep);
    }
  }

  private validateRootNode(node: BotResponseTreeNode): void {
    if (!(node.content instanceof IntentTreeNode)) {
      return;
    }

    if (node.content.nextStep) {
      const nextStepContent = node.content.nextStep.content;
      if (
        !(nextStepContent instanceof MessageBlockTreeNode) &&
        !(nextStepContent instanceof GoToIntentTreeNode) &&
        !(nextStepContent instanceof CollectVariableTreeNode) &&
        !(nextStepContent instanceof GenerativeAiTreeNode) &&
        !(nextStepContent instanceof SendEmailTreeNode) &&
        !(nextStepContent instanceof SendSmsTreeNode) &&
        !(nextStepContent instanceof DeleteVariableTreeNode) &&
        !(nextStepContent instanceof CodeBlockTreeNode) &&
        !(nextStepContent instanceof LlmTreeNode) &&
        !(nextStepContent instanceof ConditionalTreeNode) &&
        !(nextStepContent instanceof DoNothingTreeNode)
      ) {
        this.addError(
          'Utterances must be connected to a chatbot response, collect variable, generative AI or redirect to a different question'
        );
      }

      if (
        nextStepContent instanceof GoToIntentTreeNode &&
        this.currentIntentId !== null &&
        nextStepContent.intentId === this.currentIntentId
      ) {
        this.addError(
          'A question cannot redirect to itself as the first step. This would create an infinite loop.'
        );
      }

      if (
        nextStepContent instanceof MessageBlockTreeNode &&
        nextStepContent.nextStep?.content instanceof GoToIntentTreeNode &&
        this.currentIntentId !== null &&
        nextStepContent.nextStep.content.intentId === this.currentIntentId
      ) {
        this.addError(
          'A question cannot redirect to itself immediately after a chatbot response. This would create an infinite loop.'
        );
      }

      this.validateNode(node.content.nextStep);
    } else {
      this.addError(
        'Question must end in a chatbot response or redirect to a different question'
      );
    }
  }

  private validateGoToIntent(node: BotResponseTreeNode): void {
    if (!(node.content instanceof GoToIntentTreeNode)) {
      return;
    }

    if (!node.content.intentId) {
      this.addError('Redirect to a different question must specify a question');
    }
  }

  private validateConditionalNode(node: BotResponseTreeNode): void {
    const condNode = node.content as ConditionalTreeNode;

    if (!condNode.slotName) {
      this.addError('Conditional block must have a variable selected');
    }

    if (
      condNode.comparisonOperator &&
      condNode.comparisonOperator !== ComparisonOperator.IS_NULL &&
      condNode.comparisonOperator !== ComparisonOperator.BOOLEAN
    ) {
      if (!condNode.comparisonValue || condNode.comparisonValue.trim() === '') {
        this.addError('Conditional block must have a comparison value');
      }
    }

    if (!condNode.trueStep) {
      this.addError('Conditional block must have a true step');
    }

    if (!condNode.falseStep) {
      this.addError('Conditional block must have a false step');
    }

    if (condNode.trueStep) {
      this.validateNode(condNode.trueStep);
    }
    if (condNode.falseStep) {
      this.validateNode(condNode.falseStep);
    }
  }

  private static isValidConversationBlock(content: unknown): boolean {
    return (
      content instanceof MessageBlockTreeNode ||
      content instanceof ResponseCardsTreeNode ||
      content instanceof GoToIntentTreeNode ||
      content instanceof ConditionalTreeNode ||
      content instanceof CollectVariableTreeNode ||
      content instanceof GenerativeAiTreeNode ||
      content instanceof CalculationTreeNode ||
      content instanceof SendEmailTreeNode ||
      content instanceof SendSmsTreeNode ||
      content instanceof DeleteVariableTreeNode ||
      content instanceof CodeBlockTreeNode ||
      content instanceof LlmTreeNode ||
      content instanceof DoNothingTreeNode
    );
  }

  private validateCollectVariableSlotName(
    cvNode: CollectVariableTreeNode
  ): void {
    if (!cvNode.slotName || cvNode.slotName.trim() === '') {
      this.addError('Please add a distinct name for this variable');
    }
  }

  private validateCollectVariableFallbackStep(
    cvNode: CollectVariableTreeNode,
    errorMessage: string
  ): void {
    const requiresFallback =
      cvNode.slotValueType === 'NUMBER' ||
      cvNode.slotValueType === 'ENUMERATED_VALUE';

    if (!requiresFallback) {
      return;
    }

    if (!cvNode.fallbackStep) {
      this.addError('Collect variable must have a default branch');
      return;
    }

    if (!NodeValidator.isValidConversationBlock(cvNode.fallbackStep.content)) {
      this.addError(errorMessage);
    }
    this.validateNode(cvNode.fallbackStep);
  }

  private static requiresProvidedStep(
    cvNode: CollectVariableTreeNode
  ): boolean {
    return (
      cvNode.slotValueType === 'FREE_TEXT' ||
      cvNode.slotValueType === 'NUMBER' ||
      cvNode.slotValueType === 'DOCUMENT_UPLOAD' ||
      Boolean(cvNode.collectAsArray)
    );
  }

  private validateCollectVariableProvidedStep(
    cvNode: CollectVariableTreeNode,
    errorMessage: string
  ): void {
    if (
      NodeValidator.requiresProvidedStep(cvNode) &&
      !cvNode.slotProvidedStep
    ) {
      const specificError = cvNode.collectAsArray
        ? 'When "Collect as list" is enabled, the continuation prompt must be connected'
        : 'Collect variable must have a provided branch when the slot value type is FREE_TEXT, NUMBER, or DOCUMENT_UPLOAD.';
      this.addError(specificError);
      return;
    }

    if (!cvNode.slotProvidedStep) {
      return;
    }

    const providedContent = cvNode.slotProvidedStep.content;
    if (!NodeValidator.isValidConversationBlock(providedContent)) {
      this.addError(errorMessage);
    }

    if (cvNode.collectAsArray) {
      this.validateCollectAsArrayStructure(providedContent);
    }

    this.validateNode(cvNode.slotProvidedStep);
  }

  private validateCollectAsArrayStructure(providedContent: unknown): void {
    if (!(providedContent instanceof MessageBlockTreeNode)) {
      this.addError(
        'When "Collect as list" is enabled, the continuation prompt must connect to a chatbot response block'
      );
      return;
    }

    const messageNode = providedContent;
    if (!messageNode.nextStep) {
      this.addError(
        'When "Collect as list" is enabled, the continuation prompt must connect to a chatbot response block ' +
          'with follow up options (for "Add another?" prompt)'
      );
      return;
    }

    if (!(messageNode.nextStep.content instanceof ResponseCardsTreeNode)) {
      this.addError(
        'When "Collect as list" is enabled, the chatbot response after continuation prompt must be ' +
          'connected to follow up options (for "Add another?" prompt)'
      );
    }
  }

  private validateCollectVariableConditionals(
    cvNode: CollectVariableTreeNode,
    errorMessage: string
  ): void {
    cvNode.slotConditionals.forEach((conditional) => {
      if (!conditional.nextStep) {
        return;
      }

      if (
        !NodeValidator.isValidConversationBlock(conditional.nextStep.content)
      ) {
        this.addError(errorMessage);
      }
      this.validateNode(conditional.nextStep);
    });
  }

  private validateCollectVariable(node: BotResponseTreeNode): void {
    const cvNode = node.content as CollectVariableTreeNode;
    const errorMessage =
      'Collect variable branch must be connected to a valid conversation block' +
      '(chatbot response, follow up options, collect variable or redirect to a different question)';

    this.validateCollectVariableSlotName(cvNode);
    this.validateCollectVariableFallbackStep(cvNode, errorMessage);
    this.validateCollectVariableProvidedStep(cvNode, errorMessage);
    this.validateCollectVariableConditionals(cvNode, errorMessage);
  }

  private validateCalculationNode(node: BotResponseTreeNode): void {
    const calcNode = node.content as CalculationTreeNode;

    if (!calcNode.arithmeticStep && !calcNode.dateStep) {
      this.addError(
        'Calculation block must have either arithmetic or date calculation configured'
      );
    }

    if (calcNode.arithmeticStep && calcNode.dateStep) {
      this.addError(
        'Calculation block cannot have both arithmetic and date calculations'
      );
    }

    if (calcNode.arithmeticStep) {
      if (
        !calcNode.arithmeticStep.operand1Value ||
        calcNode.arithmeticStep.operand1Value.trim() === ''
      ) {
        this.addError(
          'Arithmetic calculation must have a first operand (variable)'
        );
      }
      if (
        !calcNode.arithmeticStep.operand2Value ||
        calcNode.arithmeticStep.operand2Value.trim() === ''
      ) {
        this.addError(
          'Arithmetic calculation must have a second operand (number)'
        );
      }
      if (
        !calcNode.arithmeticStep.resultKey ||
        calcNode.arithmeticStep.resultKey.trim() === ''
      ) {
        this.addError(
          'Arithmetic calculation must have a result variable name'
        );
      }
    }

    if (calcNode.dateStep) {
      if (
        !calcNode.dateStep.dateBaseValue ||
        calcNode.dateStep.dateBaseValue.trim() === ''
      ) {
        this.addError('Date calculation must have a base date variable');
      }
      if (
        !calcNode.dateStep.dateAmountValue ||
        calcNode.dateStep.dateAmountValue.trim() === ''
      ) {
        this.addError('Date calculation must have a days amount');
      }
      if (
        !calcNode.dateStep.dateResultKey ||
        calcNode.dateStep.dateResultKey.trim() === ''
      ) {
        this.addError('Date calculation must have a result variable name');
      }
    }

    if (calcNode.nextStep) {
      this.validateNode(calcNode.nextStep);
    }
  }

  private validateSendEmail(node: BotResponseTreeNode): void {
    const sendEmailNode = node.content as SendEmailTreeNode;

    if (!sendEmailNode.recipient || sendEmailNode.recipient.trim() === '') {
      this.addError('Send email block must have a recipient email address');
    }

    if (sendEmailNode.recipient && sendEmailNode.recipient.trim() !== '') {
      const recipient = sendEmailNode.recipient.trim();
      if (recipient.includes('@') && !validator.isEmail(recipient)) {
        this.addError('Send email block must have a valid email address');
      }
    }

    if (!sendEmailNode.subject || sendEmailNode.subject.trim() === '') {
      this.addError('Send email block must have an email subject');
    }

    if (!sendEmailNode.content || sendEmailNode.content.trim() === '') {
      this.addError('Send email block must have email content');
    }

    sendEmailNode.slotDisplayConditions
      ?.filter(
        (c) =>
          c.conditional &&
          c.displayBehavior === 'SHOW_FALLBACK' &&
          (!c.fallbackText || c.fallbackText.trim() === '')
      )
      .forEach((c) => {
        this.addError(
          `Variable "${c.slotName}" is set to show fallback but has no fallback text`
        );
      });

    if (sendEmailNode.nextStep) {
      this.validateNode(sendEmailNode.nextStep);
    }
  }

  private validateSendSms(node: BotResponseTreeNode): void {
    const sendSmsNode = node.content as SendSmsTreeNode;

    if (!sendSmsNode.phoneNumber || sendSmsNode.phoneNumber.trim() === '') {
      this.addError('Send SMS block must have a phone number');
    }

    if (sendSmsNode.phoneNumber && sendSmsNode.phoneNumber.trim() !== '') {
      const phoneNumber = sendSmsNode.phoneNumber.trim();
      if (!validator.isMobilePhone(phoneNumber, 'any')) {
        this.addError('Send SMS block must have a valid phone number');
      }
    }

    if (!sendSmsNode.content || sendSmsNode.content.trim() === '') {
      this.addError('Send SMS block must have SMS content');
    }

    sendSmsNode.slotDisplayConditions
      ?.filter(
        (c) =>
          c.conditional &&
          c.displayBehavior === 'SHOW_FALLBACK' &&
          (!c.fallbackText || c.fallbackText.trim() === '')
      )
      .forEach((c) => {
        this.addError(
          `Variable "${c.slotName}" is set to show fallback but has no fallback text`
        );
      });

    if (sendSmsNode.nextStep) {
      this.validateNode(sendSmsNode.nextStep);
    }
  }

  private validateDeleteVariable(node: BotResponseTreeNode): void {
    const deleteVariableNode = node.content as DeleteVariableTreeNode;

    if (
      !deleteVariableNode.variableName ||
      deleteVariableNode.variableName.trim() === ''
    ) {
      this.addError('Delete variable block must have a variable name selected');
    }

    if (deleteVariableNode.nextStep) {
      this.validateNode(deleteVariableNode.nextStep);
    }
  }

  private validateCodeBlock(node: BotResponseTreeNode): void {
    const codeBlockNode = node.content as CodeBlockTreeNode;

    if (!codeBlockNode.code || codeBlockNode.code.trim() === '') {
      this.addError('Code block must have Python code');
    }

    const envVarErrors = validateEnvironmentVariables(
      codeBlockNode.environmentVariables
    );
    envVarErrors.forEach((error) => this.addError(error));

    if (codeBlockNode.nextStep) {
      this.validateNode(codeBlockNode.nextStep);
    }
  }

  private validateLlmNode(node: BotResponseTreeNode): void {
    const llmNode = node.content as LlmTreeNode;

    if (!llmNode.prompt || llmNode.prompt.trim() === '') {
      this.addError('LLM prompt block must have a prompt');
    }

    if (
      llmNode.outputValueType === SlotValueType.BOOLEAN &&
      (!llmNode.outputVariableName || llmNode.outputVariableName.trim() === '')
    ) {
      this.addError('Output variable name is required for Boolean LLM steps');
    }

    if (llmNode.nextStep) {
      this.validateNode(llmNode.nextStep);
    }
  }

  public validate(rootNode: BotResponseTreeNode): string[] {
    this.errors = [];
    this.visitedNodes.clear();
    this.validateNode(rootNode);
    return this.errors;
  }
}

function validateNodeStructure(
  rootNode: BotResponseTreeNode,
  intentId: number | null = null
): string[] {
  const nodeValidator = new NodeValidator(intentId);
  return nodeValidator.validate(rootNode);
}

export default validateNodeStructure;
